import { useState, useEffect, useRef } from "react";
import { menuCategories, addOns } from "@/mocks/bbCoffeeData";

export default function Menu() {
  const [activeTab, setActiveTab] = useState("signature");
  const sectionRef = useRef<HTMLElement>(null);

  const activeCategory = menuCategories.find((c) => c.id === activeTab);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("revealed");
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" }
    );
    const els = sectionRef.current?.querySelectorAll(".reveal-on-scroll");
    els?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section id="menu" ref={sectionRef} className="py-20 md:py-28 bg-background-50">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center max-w-2xl mx-auto mb-12 reveal-on-scroll">
          <span className="inline-block px-4 py-1.5 mb-4 text-xs font-semibold tracking-widest uppercase text-accent-600 bg-accent-100 rounded-full">
            Our Menu
          </span>
          <h2 className="text-3xl md:text-5xl font-heading font-bold text-foreground-900 mb-4">
            Handcrafted with <span className="text-primary-600">Love</span>
          </h2>
          <p className="text-foreground-500 text-sm md:text-base">
            Every drink is made fresh to order using our own roasted beans. Explore our full range below.
          </p>
        </div>

        <div className="flex flex-wrap justify-center gap-2 mb-12 reveal-on-scroll">
          {menuCategories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveTab(cat.id)}
              className={`whitespace-nowrap px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 cursor-pointer ${
                activeTab === cat.id
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-foreground-900"
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        <div className="reveal-on-scroll mb-16" data-product-shop="true">
          <h3 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-8 text-center uppercase tracking-wide">
            {activeCategory?.label}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {activeCategory?.items.map((item) => (
              <div
                key={item.name}
                className="group bg-background-100 rounded-2xl overflow-hidden transition-all duration-500 hover:-translate-y-2 hover:bg-background-50"
              >
                <div className="aspect-square overflow-hidden bg-background-200 flex items-center justify-center">
                  <img
                    alt={item.name}
                    className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
                    src={item.image}
                  />
                </div>
                <div className="p-4 md:p-5">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h4 className="font-heading font-semibold text-foreground-900 text-base uppercase leading-tight">
                      {item.name}
                    </h4>
                  </div>
                  <div className="flex items-center gap-1.5 mt-2">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-accent-600">{item.type}</span>
                    <span className="text-sm font-bold text-primary-600 ml-auto">RM {item.price}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="reveal-on-scroll max-w-3xl mx-auto">
          <div className="text-center mb-8">
            <span className="inline-flex items-center gap-2 text-accent-600">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-star-smile-line text-lg" />
              </div>
              <span className="text-xs font-bold uppercase tracking-widest">Add Ons</span>
            </span>
            <h3 className="font-heading font-semibold text-foreground-900 text-xl mt-2">Make It Your Own</h3>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {addOns.map((addon) => (
              <div
                key={addon.name}
                className="bg-background-100 rounded-xl px-4 py-3 flex items-center justify-between transition-all duration-300 hover:bg-accent-50"
              >
                <span className="text-sm font-medium text-foreground-700">{addon.name}</span>
                <span className="text-sm font-bold text-accent-600">+RM {addon.price}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}